import { useState, useEffect, useRef } from 'react'
import { Button, Typography, Box } from '@mui/material'
import { PageHeader, Dialog } from 'Common/Supplyvue-ui'
import {
  useNavigate,
  useParams,
  unstable_usePrompt as usePrompt,
} from 'react-router-dom'
import _ from 'lodash'
import { EditCapacityTable } from './EditCapacity.table'
import {
  EditCapacityTableDataType,
  EditCapacityPageParams,
  TransformedDataType,
} from './EditCapacity.types'
import { Page } from 'Common/Page/Page'
import {
  useUpdateConstrainedPlan,
  useUpdateWorkCentreCapacityData,
  useWorkcentreCapacityData,
  useWorkcentreRunStatus,
} from './EditCapacity.data'
import { toast } from 'react-hot-toast'
import { WORKCENTRE_STATUS } from 'Common/Constants/Constants'
import { WorkcentreRunInitiatedView } from './WorkcentreRunInitiated.view'
import { useBreadcrumbsData } from 'Common/Breadcrumbs/Breadcrumbs.data'

const DialogContent = () => {
  return (
    <Box>
      <Typography sx={{ width: '100%', marginBottom: '20px' }}>
        This will recalculate the constrained plan based on the new capacity and
        may take a while to complete.
      </Typography>
    </Box>
  )
}

export const EditCapacityPage = () => {
  const [data, setData] = useState<EditCapacityTableDataType[]>([])
  const initialTableRef = useRef<EditCapacityTableDataType[]>([])
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const changedWeeksRef = useRef<Record<string, boolean>>({})
  const [isAnyWorkcentreRunning, setIsAnyWorkcentreRunning] = useState(false)
  const isAnyWorkcentreRunningRef = useRef(false)
  // The "another workcentre is being run" toast lasts 10 minutes; it must not
  // outlive this page, nor fire for the run this page has just started.
  const runningToastIdRef = useRef<string | null>(null)
  const ownRunAcceptedRef = useRef(false)
  const [isSavePending, setIsSavePending] = useState(false)
  const navigate = useNavigate()
  const { planId, sourcesiteId, workcentreId } =
    useParams<EditCapacityPageParams>()

  useBreadcrumbsData({
    plan_id: planId,
    workcentre_id: workcentreId,
  })

  const { data: workcentreRunStatus } = useWorkcentreRunStatus({
    planId,
    sourcesiteId,
    workcentreId,
  })

  const { data: capacityData, isLoading: isCapacityDataLoading } =
    useWorkcentreCapacityData(
      { planId, sourcesiteId, workcentreId },
      Boolean(workcentreRunStatus)
    )

  useEffect(() => {
    if (workcentreRunStatus) {
      setIsAnyWorkcentreRunning(
        workcentreRunStatus?.is_any_workcentre_being_run ?? false
      )
    }
  }, [workcentreRunStatus])

  useEffect(() => {
    if (isAnyWorkcentreRunning) {
      isAnyWorkcentreRunningRef.current = true
      if (!ownRunAcceptedRef.current) {
        runningToastIdRef.current = toast.error(
          'Another workcentre is being run. Check back later to edit plan for this workcentre.',
          {
            duration: 10000 * 60,
          }
        )
      }
    } else if (
      isAnyWorkcentreRunning === false &&
      isAnyWorkcentreRunningRef.current === true
    ) {
      console.log(isAnyWorkcentreRunning)
      isAnyWorkcentreRunningRef.current = false
      toast.dismiss()
      toast.success(
        'Other workcentre finished running. Now you can save your changes.'
      )
    }
  }, [isAnyWorkcentreRunning])

  useEffect(() => {
    return () => {
      if (runningToastIdRef.current) {
        toast.dismiss(runningToastIdRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (capacityData?.length > 0) {
      setData(capacityData)
    }
    // initialTableRef is used to keep the very first state of the capacity table data.
    // We keep the first state to find the change in the data so that button states can be updated.
    if (initialTableRef.current?.length === 0 && capacityData?.length > 0) {
      initialTableRef.current = [...capacityData]
    }
  }, [capacityData, initialTableRef])

  // One message for save + Update plan, and the WC list only once the run
  // has been accepted (US149969).
  const onUpdatePlanSuccess = () => {
    ownRunAcceptedRef.current = true
    toast.success('Capacity hours updated and plan run initiated.')
    navigate(`/plan/${planId}`)
  }

  // The save has already committed; say so, and stay on the page.
  const onUpdatePlanFailure = (message: string) => {
    toast.error(
      `Capacity changes were saved to the plan, but the plan run did not start: ${message.replace(/[.\s]+$/, '')}. They will be applied the next time the plan runs.`
    )
  }

  const { mutate: updateConstrainedPlan } = useUpdateConstrainedPlan(
    onUpdatePlanSuccess,
    onUpdatePlanFailure
  )

  const onUpdateCapacitySuccess = () => {
    setShowConfirmDialog(false)
    initialTableRef.current = [...data]
    // Capacity is saved; now run the plan. Its handlers toast and navigate.
    updateConstrainedPlan({
      planId,
      sourcesiteId,
      workcentreId,
    })
  }

  const onUpdateCapacityFailure = () => {
    toast.error('Update capacity hours failed.')
    setShowConfirmDialog(false)
  }

  const { isLoading: isCapacityUpdating, mutate: updateCapacityData } =
    useUpdateWorkCentreCapacityData(
      onUpdateCapacitySuccess,
      onUpdateCapacityFailure
    )

  const updateTableData = (data: EditCapacityTableDataType[]) => {
    setIsSavePending(true)
    setData([...data])
  }

  const handleUpdatePlanButtonClick = () => {
    setShowConfirmDialog(true)
  }

  const handleConfirmButtonClick = () => {
    setIsSavePending(false)
    saveUpdatedCapacityHours(data)
  }

  const handleDiscardButtonClick = () => {
    setShowCancelDialog(false)
    setIsSavePending(false)
    setTimeout(() => {
      navigate(-1)
    }, 1000)
  }

  const handleCancelClick = () => {
    _.isEqual(data, initialTableRef.current)
      ? navigate(-1)
      : setShowCancelDialog(true)
  }

  const isApplChangesButtonDisabled = () => {
    return (
      (_.isEqual(data, initialTableRef.current) && !isSavePending) ||
      isAnyWorkcentreRunning
    )
  }

  // Move usePrompt hook after function definition
  usePrompt({
    message: "You haven't updated the plan, Are you sure you want to leave.",
    when: isSavePending,
  })

  const getFilteredWeeks = (data: EditCapacityTableDataType[]) => {
    const newWeeks: Record<string, boolean> = {}
    data?.forEach((tableRow: EditCapacityTableDataType) => {
      newWeeks[tableRow?.productionDateKeyFormatted] = true
    })
    changedWeeksRef.current = { ...changedWeeksRef.current, ...newWeeks }
  }

  const saveUpdatedCapacityHours = (
    updatedData: EditCapacityTableDataType[]
  ) => {
    const transformedData: TransformedDataType[] = []
    updatedData?.forEach((capacityRow: EditCapacityTableDataType) => {
      if (changedWeeksRef.current[capacityRow.productionDateKeyFormatted]) {
        transformedData.push({
          baseSourceSiteKey: capacityRow.baseSourceSiteKey,
          dateKey: capacityRow.productionDateKey,
          workCentreCapacity: capacityRow.adjustedWorkCentreCapacity,
          workCentreKey: capacityRow.workCentreKey,
        })
      }
    })

    updateCapacityData({
      planId,
      sourcesiteId,
      workcentreId,
      updatedData: transformedData,
    })
  }

  // if (
  //   isCapacityDataLoading ||
  //   isCapacityUpdating ||
  //   workCentreRunStatusLoading
  // ) {
  //   return <Loader />
  // }

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader title="Edit work centre capacity" />
        {workcentreRunStatus === WORKCENTRE_STATUS.INITIATED ||
        workcentreRunStatus === WORKCENTRE_STATUS.RUNNING ? (
          <WorkcentreRunInitiatedView />
        ) : (
          <>
            <EditCapacityTable
              data={data ?? []}
              updateTableData={updateTableData}
              getFilteredWeeks={getFilteredWeeks}
              isLoading={isCapacityDataLoading || isCapacityUpdating}
            />
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                margin: '16px 0px',
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                sx={{ marginRight: '15px' }}
                onClick={handleCancelClick}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdatePlanButtonClick}
                disabled={isApplChangesButtonDisabled()}
              >
                Update plan
              </Button>
            </Box>
          </>
        )}
        <Dialog
          title="Unsaved changes will be lost"
          content="Are you sure you want to discard the changes?"
          primaryButtonLabel="Discard"
          secondaryButtonLabel="Cancel"
          open={showCancelDialog}
          handleClose={() => setShowCancelDialog(false)}
          handlePrimaryButtonClick={handleDiscardButtonClick}
          handleSecondaryButtonClick={() => setShowCancelDialog(false)}
        />
        <Dialog
          title="Confirm changes"
          content={<DialogContent />}
          primaryButtonLabel="Confirm"
          secondaryButtonLabel="Cancel"
          open={showConfirmDialog}
          handleClose={() => setShowConfirmDialog(false)}
          handlePrimaryButtonClick={handleConfirmButtonClick}
          handleSecondaryButtonClick={() => setShowConfirmDialog(false)}
        />
      </Box>
    </Page>
  )
}
